import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CalendarCheck, Mail, Phone, Clock, Info, User, Building2 } from 'lucide-react';

const PRIVATE_URL = process.env.REACT_APP_BOOKING_URL || '';
const CORPORATE_URL = process.env.REACT_APP_BOOKING_URL_CORPORATE || '';

export const BookingPage = () => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const widgetRef = useRef(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const type = searchParams.get('type') === 'corporate' ? 'corporate' : 'private';
  const isCorporate = type === 'corporate';

  usePageMeta('booking.title', 'seo.bookingDescription');

  const bookingUrl = useMemo(
    () => (isCorporate ? CORPORATE_URL : PRIVATE_URL),
    [isCorporate]
  );

  const isCalendly = bookingUrl && bookingUrl.includes('calendly.com');

  // Re-mount widget when URL changes by re-injecting the script
  useEffect(() => {
    if (!isCalendly) return;
    const existing = document.querySelector(
      'script[src="https://assets.calendly.com/assets/external/widget.js"]'
    );
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      try {
        document.body.removeChild(script);
      } catch {
        // already removed
      }
    };
  }, [isCalendly, bookingUrl]);

  const switchType = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'corporate') params.set('type', 'corporate');
    else params.delete('type');
    setSearchParams(params, { replace: true });
    setIframeLoaded(false);
  };

  return (
    <div className="min-h-screen" data-testid="booking-page">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-sea-blue/5 via-white to-bronze/5 py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sea-blue/10 mb-6">
              <CalendarCheck className="h-8 w-8 text-sea-blue" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('booking.title')}
            </h1>

            <span
              className={`inline-block text-sm font-semibold px-4 py-1.5 rounded-full mb-4 ${
                isCorporate
                  ? 'bg-bronze/15 text-bronze'
                  : 'bg-sea-blue/10 text-sea-blue'
              }`}
              data-testid="booking-audience-badge"
            >
              {isCorporate
                ? t('booking.corporateBadge')
                : t('booking.privateBadge')}
            </span>

            <p className="text-xl text-gray-700 mb-3">
              {isCorporate ? t('booking.corporateSubtitle') : t('booking.subtitle')}
            </p>
            <p className="text-lg text-gray-600">
              {isCorporate ? t('booking.corporateIntro') : t('booking.intro')}
            </p>
          </div>
        </div>
      </section>

      {/* Audience switcher */}
      <section className="bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="max-w-2xl mx-auto">
            <p className="text-center text-sm text-gray-500 mb-3">
              {t('booking.switchPrompt')}
            </p>
            <div
              className="flex flex-col sm:flex-row gap-3 justify-center"
              role="tablist"
              aria-label="Booking type"
            >
              <button
                onClick={() => switchType('private')}
                className={`flex items-center justify-center gap-2 px-5 py-3 rounded-lg border-2 transition-all text-sm font-semibold ${
                  !isCorporate
                    ? 'border-sea-blue bg-sea-blue text-white shadow'
                    : 'border-gray-200 text-gray-700 hover:border-sea-blue/50 hover:text-sea-blue'
                }`}
                data-testid="booking-switch-private"
                role="tab"
                aria-selected={!isCorporate}
              >
                <User className="h-4 w-4" />
                {t('booking.privateOption')}
              </button>
              <button
                onClick={() => switchType('corporate')}
                className={`flex items-center justify-center gap-2 px-5 py-3 rounded-lg border-2 transition-all text-sm font-semibold ${
                  isCorporate
                    ? 'border-bronze bg-bronze text-white shadow'
                    : 'border-gray-200 text-gray-700 hover:border-bronze/50 hover:text-bronze'
                }`}
                data-testid="booking-switch-corporate"
                role="tab"
                aria-selected={isCorporate}
              >
                <Building2 className="h-4 w-4" />
                {t('booking.corporateOption')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What to expect */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3 p-5 rounded-lg border border-gray-100 bg-gradient-to-br from-sea-blue/5 to-transparent">
              <Clock className="h-6 w-6 text-sea-blue flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  {isCorporate ? t('booking.corpFeature1Title') : t('booking.feature1Title')}
                </h3>
                <p className="text-sm text-gray-600">
                  {isCorporate ? t('booking.corpFeature1Desc') : t('booking.feature1Desc')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-5 rounded-lg border border-gray-100 bg-gradient-to-br from-bronze/5 to-transparent">
              <Info className="h-6 w-6 text-bronze flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  {isCorporate ? t('booking.corpFeature2Title') : t('booking.feature2Title')}
                </h3>
                <p className="text-sm text-gray-600">
                  {isCorporate ? t('booking.corpFeature2Desc') : t('booking.feature2Desc')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-5 rounded-lg border border-gray-100 bg-gradient-to-br from-sea-blue/5 to-transparent">
              <CalendarCheck className="h-6 w-6 text-sea-blue flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  {isCorporate ? t('booking.corpFeature3Title') : t('booking.feature3Title')}
                </h3>
                <p className="text-sm text-gray-600">
                  {isCorporate ? t('booking.corpFeature3Desc') : t('booking.feature3Desc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking widget */}
      <section className="py-12 bg-gradient-to-br from-sea-blue/5 via-white to-bronze/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            {bookingUrl ? (
              <Card className="overflow-hidden border-2 border-sea-blue/20 shadow-lg">
                <CardContent className="p-0">
                  {isCalendly ? (
                    <div
                      key={bookingUrl}
                      ref={widgetRef}
                      className="calendly-inline-widget"
                      data-url={bookingUrl}
                      style={{ minWidth: '320px', height: '720px' }}
                      data-testid="calendly-widget"
                    />
                  ) : (
                    <iframe
                      key={bookingUrl}
                      src={bookingUrl}
                      title="Book a meeting with Next Step Denmark"
                      onLoad={() => setIframeLoaded(true)}
                      style={{ border: 'none', width: '100%', height: '720px', display: 'block' }}
                      data-testid="booking-iframe"
                    />
                  )}
                  {!iframeLoaded && !isCalendly && (
                    <p className="sr-only">Loading booking calendar…</p>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card
                className="border-2 border-dashed border-bronze/40 bg-white"
                data-testid="booking-placeholder"
              >
                <CardContent className="p-10 text-center space-y-6">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-bronze/15">
                    <CalendarCheck className="h-7 w-7 text-bronze" />
                  </div>
                  <div className="space-y-3">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                      {t('booking.placeholderTitle')}
                    </h2>
                    <p className="text-gray-700 leading-relaxed max-w-2xl mx-auto">
                      {t('booking.placeholderBody')}
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
                    <a href="mailto:hello@nextstepdenmark.com">
                      <Button
                        size="lg"
                        className="bg-sea-blue hover:bg-sea-blue/90 text-white w-full sm:w-auto"
                      >
                        <Mail className="mr-2 h-5 w-5" />
                        {t('booking.placeholderEmailBtn')}
                      </Button>
                    </a>
                    <a href="tel:+4550912123">
                      <Button
                        size="lg"
                        variant="outline"
                        className="border-sea-blue text-sea-blue hover:bg-sea-blue hover:text-white w-full sm:w-auto"
                      >
                        <Phone className="mr-2 h-5 w-5" />
                        {t('booking.placeholderPhoneBtn')}
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Fallback contact note */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              {t('booking.cantFindTitle')}
            </h2>
            <p className="text-gray-700 mb-6">{t('booking.cantFindBody')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:hello@nextstepdenmark.com">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-sea-blue text-sea-blue hover:bg-sea-blue hover:text-white w-full sm:w-auto"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  hello@nextstepdenmark.com
                </Button>
              </a>
              <a href="tel:+4550912123">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-sea-blue text-sea-blue hover:bg-sea-blue hover:text-white w-full sm:w-auto"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  +45 50 91 21 23
                </Button>
              </a>
            </div>
            {/* link to opposite path */}
            <p className="mt-8 text-sm text-gray-500">
              {isCorporate ? (
                <Link to="/booking" className="text-sea-blue hover:underline">
                  {t('booking.linkPrivate')}
                </Link>
              ) : (
                <Link to="/booking?type=corporate" className="text-bronze hover:underline">
                  {t('booking.linkCorporate')}
                </Link>
              )}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
